import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiX, HiCheckCircle, HiSearchCircle } from 'react-icons/hi';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { useCart } from '../../context/CartContext';
import { Link } from 'react-router-dom';

const PlanModal = ({ plan, onClose }) => {
    const [selectedImg, setSelectedImg] = useState(0);
    const { addToCart, cart } = useCart();
    const isInCart = cart.some(item => item.id === plan.id);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, []);

    if (!plan) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[1000] flex items-center justify-center p-2 md:p-4 bg-[#1C1C1C]/90 backdrop-blur-md">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0"
                />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    className="relative bg-white w-full max-w-6xl rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[95vh] md:max-h-[85vh]"
                >
                    {/* Botón Cerrar */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-white shadow-lg hover:bg-[#F26B1D] hover:text-white rounded-full transition-all z-30"
                    >
                        <HiX className="text-xl" />
                    </button>

                    {/* COLUMNA IZQUIERDA: GALERÍA */}
                    <div className="md:w-3/5 bg-gray-50 flex flex-col p-4 border-r border-gray-100 overflow-hidden">
                        <div className="relative flex-1 flex items-center justify-center bg-white rounded-2xl overflow-hidden shadow-inner">
                            <div className="absolute top-4 left-4 z-10 bg-[#1C1C1C]/60 text-white text-[10px] px-3 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
                                <HiSearchCircle className="text-sm" /> Click para ampliar
                            </div>
                            <Zoom overlayBgColorEnd="rgba(28, 28, 28, 0.95)">
                                <img
                                    src={plan.images[selectedImg]}
                                    alt={plan.name}
                                    className="max-w-full max-h-[40vh] md:max-h-[55vh] object-contain cursor-zoom-in"
                                />
                            </Zoom>
                        </div>

                        <div className="flex gap-3 mt-4 justify-center overflow-x-auto pb-2 h-24">
                            {plan.images.map((img, index) => (
                                <button
                                    key={index}
                                    onClick={() => setSelectedImg(index)}
                                    className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 ${selectedImg === index ? 'border-[#F26B1D] scale-105' : 'border-transparent opacity-50'
                                        }`}
                                >
                                    <img src={img} className="w-full h-full object-cover" alt="thumbnail" />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* COLUMNA DERECHA: INFO */}
                    <div className="md:w-2/5 flex flex-col bg-white overflow-hidden">
                        {/* Contenido con Scroll */}
                        <div className="flex-1 overflow-y-auto p-8 pb-4">
                            <div className="flex flex-wrap gap-2 mb-3">

                                <span className="text-[#F26B1D] font-bold text-xs uppercase tracking-widest bg-[#F26B1D]/10 px-3 py-1 rounded-md">
                                    {plan.category}
                                </span>

                                {/* Plan Type */}
                                <span className="text-[10px] font-black bg-gray-100 px-3 py-1 rounded-full text-gray-700">
                                    {plan.planType === "Diseño" ? "✏️ Solo diseño" : "📦 Planos completos"}
                                </span>

                                {/* Package */}
                                {plan.packageType === "Completo" && (
                                    <span className="text-[10px] font-black bg-[#F26B1D] text-white px-3 py-1 rounded-full">
                                        🔥 Paquete completo
                                    </span>
                                )}

                                {/* Badge */}
                                {plan.badge && (
                                    <span className="text-[10px] font-black bg-green-100 text-green-700 px-3 py-1 rounded-full">
                                        {plan.badge}
                                    </span>
                                )}
                            </div>

                            <h2 className="text-3xl font-black text-[#1C1C1C] mt-3 leading-tight uppercase">
                                {plan.name}
                            </h2>

                            <p className="text-gray-500 text-sm mt-4 leading-relaxed">
                                {plan.description}
                            </p>

                            <div className="grid grid-cols-2 gap-2 my-6">
                                {[
                                    { label: 'Lote', val: `${plan.front}x${plan.depth}` },
                                    { label: 'Niveles', val: plan.floors },
                                    { label: 'Área', val: plan.area },
                                    { label: 'Cuartos', val: plan.rooms }
                                ].map((spec, i) => (
                                    <div key={i} className="p-2 bg-gray-50 rounded-xl border border-gray-100 text-center">
                                        <p className="text-[9px] text-gray-400 font-bold uppercase">{spec.label}</p>
                                        <p className="text-xs font-black text-gray-800">{spec.val}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="mb-6">
                                <h4 className="text-xs font-black text-[#1C1C1C] mb-3 flex items-center gap-2 uppercase tracking-tighter">
                                    <HiCheckCircle className="text-[#F26B1D] text-lg" /> Incluye documentación
                                </h4>
                                <ul className="space-y-1.5">
                                    {plan.includes.map((item, i) => (
                                        <li key={i} className="text-[12px] text-gray-600 flex items-center gap-2">
                                            <div className="w-1 h-1 bg-[#F26B1D] rounded-full" /> {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {plan.bonus && (
                                <div className="mb-6">
                                    <h4 className="text-xs font-black text-[#1C1C1C] mb-3 flex items-center gap-2 uppercase tracking-tighter">
                                        🎁 Beneficios adicionales
                                    </h4>
                                    <ul className="space-y-1.5">
                                        {plan.bonus.map((item, i) => (
                                            <li key={i} className="text-[12px] text-gray-600 flex items-center gap-2">
                                                <div className="w-1 h-1 bg-green-500 rounded-full" /> {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 mb-4">
                                <div className="flex gap-3">
                                    <span className="text-lg">⚠️</span>
                                    <div className="space-y-2">
                                        <h4 className="text-[10px] font-black text-amber-800 uppercase tracking-widest">
                                            Aviso Importante
                                        </h4>
                                        <p className="text-[10px] leading-snug text-amber-700/90">
                                            Diseño referencial. Requiere <b>estudio de suelos</b> y validación de un <b>profesional colegiado</b>.
                                        </p>

                                        {/* Botones de Acción Rápida */}
                                        <div className="flex gap-2 pt-1">
                                            <Link
                                                to="/preguntas-frecuentes"
                                                className="bg-amber-100 hover:bg-amber-200 text-amber-900 text-[9px] font-black uppercase tracking-tighter px-3 py-1.5 rounded-lg transition-colors border border-amber-200/50 flex items-center gap-1"
                                            >
                                                <span>❓</span> Preguntas
                                            </Link>
                                            <Link
                                                to="/terminos-y-condiciones"
                                                className="bg-amber-100 hover:bg-amber-200 text-amber-900 text-[9px] font-black uppercase tracking-tighter px-3 py-1.5 rounded-lg transition-colors border border-amber-200/50 flex items-center gap-1"
                                            >
                                                <span>📜</span> Términos
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* FOOTER FIJO (PRECIO + BOTÓN) */}
                        <div className="p-6 border-t bg-gray-50/50 flex flex-col md:flex-row md:items-center md:justify-between gap-4">

                            {/* PRECIO */}
                            <div>
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                    Inversión
                                </p>

                                <div className="flex items-end gap-3">

                                    {/* PRECIO PRINCIPAL */}
                                    <div className="flex flex-col">
                                        {plan.originalPrice && (
                                            <span className="text-xs text-gray-400 line-through">
                                                S/ {plan.originalPrice}
                                            </span>
                                        )}

                                        <span className="text-3xl font-black text-[#1C1C1C] leading-none">
                                            S/ {plan.price}
                                        </span>
                                    </div>

                                    {/* USD */}
                                    <div className="bg-white border border-gray-200 px-3 py-1.5 rounded-xl shadow-sm">
                                        <span className="text-[10px] text-gray-400 font-bold block leading-none">
                                            USD
                                        </span>
                                        <span className="text-sm font-black text-[#1C1C1C] leading-none">
                                            {Math.round(plan.price / 3.8)}
                                        </span>
                                    </div>

                                </div>
                            </div>

                            {/* BOTÓN */}
                            <button
                                onClick={() => { if (!isInCart) addToCart(plan); onClose(); }}
                                disabled={isInCart}
                                className={`w-full md:w-auto ${isInCart
                                        ? 'bg-gray-200 text-gray-500'
                                        : 'bg-[#1C1C1C] hover:bg-[#F26B1D] text-white shadow-lg'
                                    } px-6 py-4 md:py-3.5 rounded-2xl font-black uppercase text-xs tracking-widest transition-all active:scale-95`}
                            >
                                {isInCart ? 'En carrito' : 'Comprar ahora'}
                            </button>

                        </div>

                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default PlanModal;